/* eslint-disable no-shadow */
/* eslint-disable object-curly-newline */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import handleKeyPress from '../../../common/handleKeyPress';
import areBlocksEqual from '../../../common/areBlocksEqual';
import Button from '../../../componentLibrary/Button';
import CustomTooltip from '../../../componentLibrary/CustomTooltip';
import { resetAutoSaveTimer, setIsSaveFired, stopAutoSaveTrigger } from '../../../middleware/autoSave';
import rules from '../../../constants/businessRules/businessRules';
import { APPLICATION_MODES } from '../../../constants/itemTypes/itemTypes';
import * as savingActions from '../../../actions/saving';
import { blockType } from '../../../types';
import { COLORS } from '../../../common/branding/colors';
import * as Styled from './styles';

/**
 * This function returns the date verbiage under design doc specs
 * @param {number} date - Unix timestamp in seconds (seconds since the Unix Epoch)
 * @return {string} - Formatted date verbiage
 */
const _getDateVerbiage = (date) => {
    const momentDateSaved = moment.unix(date);

    // Using calendar() we get the locale timezone adjusted, we don't need to deal with this
    const dateSavedVerbiage = momentDateSaved.calendar(); // Today at 10:52 AM

    if (dateSavedVerbiage.indexOf('Today') !== -1) {
        return `Saved ${dateSavedVerbiage.slice(6)}`;
    }

    if (dateSavedVerbiage.indexOf('Yesterday') !== -1) {
        return `Saved ${dateSavedVerbiage.charAt(0).toLowerCase() + dateSavedVerbiage.slice(1)}`;
    }

    return `Saved on ${momentDateSaved.format('D MMM YYYY')} at ${momentDateSaved.format('h:mm A')}`;
};

const SaveButton = ({
    hasVersionHistoryAccess,
    hasSavingAccess,
    isVersionHistoryItemSaving,
    latestVersionHistorySavedItem,
    controls,
    userEmail,
    setApplicationMode,
    blocks,
    dispatch,
    saving,
    ipAddress,
    isConnected,
}) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const [savedVerbiage, setSavedVerbiage] = useState('');

    useEffect(() => {
        const latestSavedBlocks = _get(latestVersionHistorySavedItem, 'blocks', []);
        setIsEnabled(blocks.length !== 0 && !areBlocksEqual(blocks, latestSavedBlocks));
        resetAutoSaveTimer();
    }, [latestVersionHistorySavedItem, blocks]);

    useEffect(() => {
        const latestSavedDate = _get(latestVersionHistorySavedItem, 'dateSaved', 0);
        if (latestSavedDate === 0) {
            return;
        }

        let dateSavedVerbiage = _getDateVerbiage(latestSavedDate);

        const latestSavedEmail = _get(latestVersionHistorySavedItem, 'email', '');
        const latestSavedFirstName = _get(latestVersionHistorySavedItem, 'firstName', '');
        const lastName = _get(latestVersionHistorySavedItem, 'lastName', '');
        if (userEmail !== latestSavedEmail) {
            dateSavedVerbiage += ` by ${latestSavedFirstName} ${
                lastName.length <= 6 || lastName === 'Support' ? lastName : lastName[0]
            }`;
        }

        setSavedVerbiage(dateSavedVerbiage);
    }, [latestVersionHistorySavedItem, userEmail]);

    const onLastSavedTextClick = () => {
        const isAllowedToVersionHistory = rules.BANANATAG_OFFICE_IPS.includes(ipAddress) || hasVersionHistoryAccess;
        const isSaveHappening = saving || isVersionHistoryItemSaving;

        if (isAllowedToVersionHistory && !isSaveHappening) {
            setApplicationMode(APPLICATION_MODES.VERSION_HISTORY)();
        }
    };

    // Always allow access to VH btn when using VPN to allow support and sales to access it for clients
    const showVersionHistorySaveBtn =
        rules.BANANATAG_OFFICE_IPS.includes(ipAddress) ||
        (hasVersionHistoryAccess && (saving || isVersionHistoryItemSaving || savedVerbiage));

    return (
        <>
            {
                // used by public pikasso preview
                !hasSavingAccess && !hasVersionHistoryAccess && (
                    <>
                        <Styled.ButtonDiv>
                            <Button
                                type="simple"
                                width={30}
                                iconSize={18}
                                icon="save"
                                color="#BFC8D0"
                                cursor="not-allowed"
                                hasHoverEffects={false}
                                dataFor="disabledSaveButton"
                                dataTestId="Save"
                            />
                        </Styled.ButtonDiv>
                        <CustomTooltip
                            dataRef="disabledSaveButton"
                            offset={{ top: 10 }}
                            width={146}
                            text="Saving is unavailable in this preview."
                        />
                        <Styled.LastSavedDiv>
                            <Styled.DisabledLastSavedText data-tip data-for="disabledVersionHistory">
                                Saved yesterday, 3:30 by June Cha
                            </Styled.DisabledLastSavedText>
                        </Styled.LastSavedDiv>
                        <CustomTooltip
                            dataRef="disabledVersionHistory"
                            offset={{ top: 6 }}
                            text="Version History helps resolve conflicts or recover previous work. Learn more with a full product demo."
                        />
                    </>
                )
            }
            {hasSavingAccess && controls.includes('save') && (
                <Styled.ButtonDiv data-tip data-for="saveButton">
                    <Button
                        label={isEnabled || saving || isVersionHistoryItemSaving ? 'Save' : 'All changes saved'}
                        type="simple"
                        width={30}
                        iconSize={18}
                        disable={saving || isVersionHistoryItemSaving}
                        icon={saving || isVersionHistoryItemSaving ? 'sync' : 'save'}
                        iconAnimation={saving || isVersionHistoryItemSaving ? 'spin 2s linear infinite' : ''}
                        color={
                            saving || isVersionHistoryItemSaving
                                ? '#0277BD'
                                : isEnabled
                                ? 'rgb(90, 88, 88)'
                                : 'lightgray'
                        }
                        onClick={() => {
                            if (isEnabled && !saving) {
                                dispatch(savingActions.updateSavingStatus(true));
                                stopAutoSaveTrigger();
                                setIsSaveFired(true);
                            }
                        }}
                        hasHoverEffects={isEnabled}
                        dataFor="Save"
                        dataTestId="Save"
                    />
                    {!isConnected && (
                        <>
                            <ReactTooltip
                                multiline
                                id="saveButton"
                                place="bottom"
                                type="dark"
                                effect="solid"
                                offset={{ bottom: -9 }}
                            >
                                <Styled.SaveTooltipText>
                                    There may be saving
                                    <br />
                                    conflicts with
                                    <br />
                                    multiple users.
                                </Styled.SaveTooltipText>
                            </ReactTooltip>
                            <Styled.ConnectionErrorIcon style={{ color: COLORS.banana700 }} icon="exclamation-circle" />
                        </>
                    )}
                </Styled.ButtonDiv>
            )}
            {showVersionHistorySaveBtn && (
                <Styled.LastSavedDiv>
                    <Styled.LastSavedText
                        data-tip
                        data-for="saveTooltip"
                        id="version-history"
                        role="button"
                        tabIndex="0"
                        aria-label="See version history"
                        aria-live="polite"
                        aria-atomic="true"
                        isVersionHistoryItemSaving={saving || isVersionHistoryItemSaving}
                        isClickable={
                            rules.BANANATAG_OFFICE_IPS.includes(ipAddress) ||
                            (hasVersionHistoryAccess && !saving && !isVersionHistoryItemSaving)
                        }
                        onClick={() => onLastSavedTextClick()}
                        onKeyDown={(e) => handleKeyPress(e, onLastSavedTextClick)}
                    >
                        {saving || isVersionHistoryItemSaving ? 'Saving...' : savedVerbiage}
                    </Styled.LastSavedText>
                    {hasVersionHistoryAccess && !saving && !isVersionHistoryItemSaving && (
                        <ReactTooltip id="saveTooltip" place="bottom" type="dark" effect="solid" offset={{ bottom: 6 }}>
                            <Styled.SaveTooltipText>See Version History</Styled.SaveTooltipText>
                        </ReactTooltip>
                    )}
                </Styled.LastSavedDiv>
            )}
        </>
    );
};

const mapStateToProps = (state) => {
    // set socket auto-save default value as true because if there's any socket error,
    // the save will be enabled for all users instead of none.
    let hasSocketAutoSaveEnabled = true;
    const connectedClients = _get(state, 'session.collaboration.connectedClients', []);
    const currentUserSocketId = _get(state, 'session.collaboration.currentUserSocketId', '');
    const currentUserIndex = connectedClients.findIndex((client) => client.socketId === currentUserSocketId);

    if (currentUserIndex >= 0) {
        hasSocketAutoSaveEnabled = connectedClients[currentUserIndex].hasAutoSaveEnabled;
    }

    return {
        documentType: _get(state, 'session.collaboration.type', 'draft'),
        hasVersionHistoryAccess:
            (_get(state, 'session.user.plan', '') === 'IC_ENTERPRISE' ||
                _get(state, 'session.user.plan', '') === 'IC_SBENTERPRISE') &&
            _get(state, 'session.features.F_VERSION_HISTORY', false),
        hasSavingAccess: _get(state, 'session.features.F_SAVE', false),
        isVersionHistoryItemSaving: _get(state, 'versionHistory.isVersionHistoryItemSaving', false),
        latestVersionHistorySavedItem: _get(state, 'versionHistory.latestVersionHistorySavedItem', {}),
        controls: _get(state, 'session.controls', []),
        userEmail: _get(state, 'session.user.email', ''),
        blocks: _get(state, 'canvasBlocks.present', []),
        hasSocketAutoSaveEnabled,
        saving: _get(state, 'saving'),
        ipAddress: _get(state, 'session.user.ipAddress', ''),
        isConnected: _get(state, 'session.collaboration.isConnected', false),
    };
};

SaveButton.propTypes = {
    hasVersionHistoryAccess: PropTypes.bool.isRequired,
    hasSavingAccess: PropTypes.bool.isRequired,
    isVersionHistoryItemSaving: PropTypes.bool.isRequired,
    latestVersionHistorySavedItem: PropTypes.object.isRequired,
    controls: PropTypes.arrayOf(PropTypes.string).isRequired,
    userEmail: PropTypes.string.isRequired,
    setApplicationMode: PropTypes.func.isRequired,
    blocks: PropTypes.arrayOf(blockType).isRequired,
    dispatch: PropTypes.func.isRequired,
    saving: PropTypes.bool.isRequired,
    ipAddress: PropTypes.string.isRequired,
    isConnected: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(SaveButton);
